"use client";
import { useMemo } from "react";
import useSWR from "swr";
import clientSearch from "@/lib/algolia/clientSearch";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import getFilters from "@/components/Search/utils/getFilters";
import type { RelatedProductsFilter } from "@/monarch/types/ProductCompare";
import type { ProcessedCourse } from "@/common/course/types";
import {
  buildCompareFacetFilters,
  canFetchRelatedProducts,
  collectCategoryFilters,
  collectPartnerFilters,
  collectSkillFilters,
  collectSubjectFilters,
  resolveRelatedProductsFilters,
} from "./buildCompareFacetFilters";

const DEFAULT_RELATED_COURSE_LIMIT = 6;

interface AlgoliaCourseHit {
  uuid?: string;
  productUuid?: string;
  objectID: string;
}

const normalizeUuid = (hit: AlgoliaCourseHit): string => {
  const raw = hit.productUuid ?? hit.uuid ?? hit.objectID;
  return raw.replace(/^course-/, "");
};

export interface UseRelatedCoursesResult {
  uuids: string[];
  isLoading: boolean;
}

export interface UseRelatedCoursesOptions {
  course?: ProcessedCourse;
  enabled?: boolean;
  limit?: number;
  relatedProductsFilters?: RelatedProductsFilter[];
}

export function useRelatedCourses({
  course,
  enabled = true,
  limit = DEFAULT_RELATED_COURSE_LIMIT,
  relatedProductsFilters,
}: UseRelatedCoursesOptions): UseRelatedCoursesResult {
  const indexName = PRODUCT_INDEX_EN as string;
  const currentUuid = course?.productUuid?.replace(/^course-/, "");
  const enabledFilters = resolveRelatedProductsFilters(
    relatedProductsFilters,
    "course",
    "courses",
  );

  const subjectFilters = useMemo(
    () =>
      enabledFilters.includes("subject")
        ? collectSubjectFilters(course, indexName)
        : [],
    [course, enabledFilters, indexName],
  );
  const skillFilters = useMemo(
    () =>
      enabledFilters.includes("skill")
        ? collectSkillFilters(course, indexName)
        : [],
    [course, enabledFilters, indexName],
  );
  const categoryFilters = useMemo(
    () =>
      enabledFilters.includes("category") ? collectCategoryFilters(course) : [],
    [course, enabledFilters],
  );
  const partnerFilters = useMemo(
    () =>
      enabledFilters.includes("sameBrand")
        ? collectPartnerFilters(course, indexName)
        : [],
    [course, enabledFilters, indexName],
  );

  const shouldFetch =
    enabled && canFetchRelatedProducts({ product: course, enabledFilters });

  const swrKey = shouldFetch
    ? [
        "product-compare-related-courses",
        currentUuid,
        enabledFilters.join("|"),
        subjectFilters.join("|"),
        skillFilters.join("|"),
        categoryFilters.join("|"),
        partnerFilters.join("|"),
        limit,
      ]
    : null;

  const { data, isLoading } = useSWR(
    swrKey,
    async () => {
      if (!currentUuid) return [];

      const facetFilters = buildCompareFacetFilters({
        currentUuid,
        enabledFilters,
        product: course,
        indexName,
      });

      const response = await clientSearch<AlgoliaCourseHit>([
        {
          indexName,
          params: {
            filters: getFilters({ productType: "course", indexName }),
            facetFilters,
            hitsPerPage: limit,
            clickAnalytics: false,
          },
        },
      ]);

      const hits = response.results[0]?.hits ?? [];
      return hits
        .map((hit) => normalizeUuid(hit))
        .filter((uuid): uuid is string => Boolean(uuid) && uuid !== currentUuid)
        .slice(0, limit);
    },
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  return {
    uuids: data ?? [],
    isLoading: shouldFetch && Boolean(isLoading),
  };
}

export default useRelatedCourses;
